import { useGetStoredDnsRecords } from "@/hooks/use-postmaster";
import { DnsRecordsResponse } from "@/types/postman.types";
import { useMemo, useState } from "react";
import { EmailHealthContext } from "./email-health-context";
import EmailHealthReportSkeleton from "./email-health-skeleton";
import StatusScreen from "@/components/status-screen";
import { InfoIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

export type EmailHealthState = {
  isPrinting: boolean;
  openSections: Record<number, boolean>;
  isAllExpanded: boolean;
};

export const EmailHealthProvider = ({
  children,
  domain,
  espType,
}: {
  children: React.ReactNode;
  domain: string;
  espType: string;
}) => {
  const [state, setState] = useState<EmailHealthState>({
    isPrinting: false,
    openSections: {},
    isAllExpanded: false,
  });

  const { data: recordsData, isFetching: isDnsCheckLoading } =
    useGetStoredDnsRecords(domain, espType);

  const transformedRecords = useMemo(() => {
    if (!recordsData) return [];

    return Object.values(recordsData?.structuredRecords || {}).map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (record: any) => {
        if (record.type === "DNSSEC") {
          return {
            type: record.type,
            status: getRecordStatusDNSSEC(recordsData),
            partialValid: getRecordPartialValidDNSSEC(recordsData),
            description: getRecordDescription(record),
            record: getRecordRecordDNSSEC(recordsData),
            result: getRecordResultDNSSEC(recordsData),
          };
        }
        
        // Check if record has actual data, not just result: true
        const hasActualData = record.record && (
          Array.isArray(record.record) ? record.record.length > 0 : 
          typeof record.record === 'object' ? Object.keys(record.record).length > 0 :
          Boolean(record.record)
        );
        
        return {
          type: record.type,
          status: record?.result && hasActualData ? "valid" : "invalid",
          description: getRecordDescription(record),
          record: record.record,
          result: record?.result && hasActualData,
        };
      }
    );
  }, [recordsData]);

  // Sort records by priority
  const sortedRecords = useMemo(() => {
    if (!transformedRecords) return [];

    return transformedRecords.sort((a, b) => {
      const priorityOrder = ["DMARC", "DKIM", "SPF", "MX", "ARC"];
      const aIndex = priorityOrder.indexOf(a.type);
      const bIndex = priorityOrder.indexOf(b.type);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return a.type.localeCompare(b.type);
    });
  }, [transformedRecords]);

  const toggleCollapseAll = (type: "collapse" | "expand") => {
    const newState: Record<number, boolean> = {};
    sortedRecords.forEach((_, index) => {
      newState[index] = type === "expand" ? true : false;
    });
    setState((prev) => ({
      ...prev,
      openSections: newState,
      isAllExpanded: type === "expand" ? true : false,
    }));
  };

  if (isDnsCheckLoading) {
    return <EmailHealthReportSkeleton />;
  }

  if (!recordsData) {
    // return <div>No records found</div>;
    return (
      <StatusScreen
        title="No Email Health Report Found"
        description="The email health report you are looking for is not available."
        icon={<InfoIcon />}
        variant="link-button"
        buttonText="Run a New Test"
        buttonLink={"/email-health"}
        wrapperClass="min-h-[calc(100vh-150px)] flex justify-center items-center w-full"
      />
    );
  }

  return (
    <EmailHealthContext.Provider
      value={{
        recordsData: recordsData,
        isLoading: isDnsCheckLoading,
        state,
        setState,
        toggleCollapseAll,
        sortedRecords,
      }}
    >
      {children}
    </EmailHealthContext.Provider>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getRecordDescription = (record: any) => {
  // Check if record has actual data
  const hasActualData = record.record && (
    Array.isArray(record.record) ? record.record.length > 0 : 
    typeof record.record === 'object' ? Object.keys(record.record).length > 0 :
    Boolean(record.record)
  );

  if (!hasActualData) {
    return "No record found";
  }

  switch (record.type) {
    case "DMARC":
      return `p=${record.record?.record?.policy || "none"} (${
        record.record?.record?.pct || 100
      }%)`;
    case "SPF":
      return record.record?.record?.rr || "No SPF record";
    case "DKIM": {
      const dkim = record.record?.record?.[0];
      return dkim ? `${dkim.selector}@${dkim.signingDomain}` : "No DKIM record";
    }
    case "AAAA":
      return record.record?.record?.length > 0 ? `${record.record.record.length} IPv6 address(es)` : "No AAAA record";
    case "STARTTLS":
      return record.record?.record?.length > 0 ? "STARTTLS supported" : "No STARTTLS record";
    case "RPKI":
      return record.record?.record?.length > 0 ? "RPKI configured" : "No RPKI record";
    case "BIMI":
      return record.record?.record ? "BIMI record found" : "No BIMI record";
    case "DANE":
      return record.record?.record ? "DANE record found" : "No DANE record";
    case "DNSKEY":
      return record.record?.record ? "DNSKEY record found" : "No DNSKEY record";
    case "MX":
      return record.record?.record ? "MX record found" : "No MX record";
    case "PTR":
      return record.record?.record ? "PTR record found" : "No PTR record";
    default:
      return record.description || record.type;
  }
};

const getRecordStatusDNSSEC = (recordsData: DnsRecordsResponse) => {
  // Check both mailServerReports.mailServerDnssec and domainReport.mailServerDnssec.dnssec
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (recordsData as any)?.mailServerReports.every(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (report: any) => report.mailServerDnssec?.isDnssecSigned
  ) && recordsData?.domainReport?.mailServerDnssec?.isValid
    ? "valid"
    : "invalid";
};

const getRecordPartialValidDNSSEC = (recordsData: DnsRecordsResponse) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (recordsData as any)?.mailServerReports.some(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (report: any) => report.mailServerDnssec?.isDnssecSigned
  ) || recordsData?.domainReport?.mailServerDnssec?.isValid
    ? "valid"
    : "invalid";
};

const getRecordRecordDNSSEC = (recordsData: DnsRecordsResponse) => {
  // Return registrar information if available
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mailserver: (recordsData as any)?.mailServerReports[0]?.mailServerDnssec
      ?.domain,
    domain: recordsData?.domainReport?.mailServerDnssec?.registrar,
  };
};

const getRecordResultDNSSEC = (recordsData: DnsRecordsResponse) => {
  // Return more detailed result information
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mailServerDnssec = (recordsData as any)?.mailServerReports[0]
    ?.mailServerDnssec;
  const domainDnssec = recordsData?.domainReport?.mailServerDnssec;

  if (!mailServerDnssec && !domainDnssec) return "No DNSSEC data available";

  if (mailServerDnssec?.errors && mailServerDnssec.errors.length > 0) {
    return false;
  }

  const isDnssecSigned =
    mailServerDnssec?.isDnssecSigned && domainDnssec?.isDnssecSigned;
  return isDnssecSigned ? true : false;
};
